/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PauseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { vmApi } from '../../services/vmApi';

interface VMLogsProps {
  vmId: string;
  projectId?: string;
  vmName: string;
  isRunning: boolean;
}

interface LogMessage {
  type: 'connected' | 'log' | 'error' | 'end';
  data?: string;
  message?: string;
  error?: string;
  pod?: string;
}

const VMLogs: React.FC<VMLogsProps> = ({
  vmId,
  projectId,
  vmName,
  isRunning,
}) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tailLines, setTailLines] = useState(100);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [podName, setPodName] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Handle scroll to detect if user scrolled up
  const handleScroll = useCallback(() => {
    if (!logsContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  }, []);

  // Connect to WebSocket for streaming logs
  const startStreaming = useCallback(() => {
    if (!isRunning) {
      setError('VM must be running to stream logs');
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsLoading(true);
    setError(null);
    // Clear logs when starting a fresh stream
    setLogs([]);

    const wsUrl = vmApi.getLogsStreamUrl(vmId, projectId, tailLines, true);
    console.log('[VMLogs] Starting log stream:', { wsUrl, vmId, projectId, tailLines });

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[VMLogs] WebSocket connected');
        setIsConnected(true);
        setIsStreaming(true);
        setIsLoading(false);
        setError(null);
        
        // Set up keepalive ping every 30 seconds
        keepAliveIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping' }));
            } catch (err) {
              console.error('[VMLogs] Keepalive ping failed:', err);
            }
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const message: LogMessage = JSON.parse(event.data);
          console.log('[VMLogs] Received message:', message.type, message);

          switch (message.type) {
            case 'connected':
              if (message.pod) {
                setPodName(message.pod);
              }
              // If we have initial logs from previous fetch, keep them
              break;
            case 'log':
              if (message.data) {
                const logLine = message.data.trim();
                if (logLine) {
                  setLogs((prev) => [...prev, logLine]);
                }
              }
              break;
            case 'error':
              const errorMsg = message.error || 'Unknown error occurred';
              console.error('[VMLogs] Received error from server:', errorMsg);
              setError(errorMsg);
              setIsStreaming(false);
              setIsConnected(false);
              // Don't close WebSocket here, let it close naturally
              break;
            case 'end':
              console.log('[VMLogs] Stream ended');
              setIsStreaming(false);
              setIsConnected(false);
              break;
          }
        } catch (parseError) {
          // If not JSON, treat as plain text log
          console.log('[VMLogs] Received non-JSON message, treating as log:', event.data);
          const logLine = event.data.trim();
          if (logLine) {
            setLogs((prev) => [...prev, logLine]);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('[VMLogs] WebSocket error:', error);
        setError('WebSocket connection error. Please try refreshing logs.');
        setIsConnected(false);
        setIsStreaming(false);
        setIsLoading(false);
      };

      ws.onclose = (event) => {
        console.log('[VMLogs] WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          vmId,
        });
        
        // Clear keepalive interval
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        
        setIsConnected(false);
        setIsStreaming(false);
        setIsLoading(false);
        
        // If closed unexpectedly (not by user), show error
        // 1000 = Normal Closure
        // 1001 = Going Away
        // 1008 = Policy Violation (auth error)
        // 1011 = Internal Error
        if (event.code === 1008) {
          setError('Authentication failed. Please refresh the page and try again.');
        } else if (event.code === 1011) {
          setError('Server error occurred. Please try again.');
        } else if (event.code !== 1000 && event.code !== 1001 && !event.wasClean) {
          setError('Log stream connection closed unexpectedly. Please try again.');
        } else if (event.code === 1000 && event.wasClean) {
          // Normal closure, clear any previous errors
          setError(null);
        }
      };
    } catch (err: any) {
      console.error('[VMLogs] Failed to create WebSocket:', err);
      setError(`Failed to connect to log stream: ${err.message || 'Unknown error'}`);
      setIsLoading(false);
    }
  }, [vmId, projectId, tailLines, isRunning]);

  // Stop streaming
  const stopStreaming = useCallback(() => {
    // Clear keepalive interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User stopped streaming');
      wsRef.current = null;
    }
    setIsStreaming(false);
    setIsConnected(false);
  }, []);

  // Fetch logs (non-streaming)
  const fetchLogs = useCallback(async () => {
    // Stop streaming if active
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsStreaming(false);
      setIsConnected(false);
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await vmApi.getVMLogs(vmId, tailLines);
      setLogs(response.logs || []);
      if (response.pod_name) {
        setPodName(response.pod_name);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch logs');
    } finally {
      setIsLoading(false);
    }
  }, [vmId, tailLines]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear keepalive interval
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // Download logs as file
  const downloadLogs = () => {
    const content = logs.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vmName}-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <DocumentTextIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-white">VM Logs</h3>
          {isConnected && (
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              Live
            </span>
          )}
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Tail lines selector */}
          <select
            value={tailLines}
            onChange={(e) => setTailLines(Number(e.target.value))}
            className="text-sm border border-gray-600 rounded-lg px-2 py-1 bg-gray-800 text-white"
            disabled={isStreaming}
          >
            <option value={50}>50 lines</option>
            <option value={100}>100 lines</option>
            <option value={500}>500 lines</option>
            <option value={1000}>1000 lines</option>
          </select>

          {/* Stream/Stop button */}
          {isRunning && (
            isStreaming ? (
              <button
                onClick={stopStreaming}
                className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300 px-2 py-1 rounded-lg hover:bg-orange-500/20"
              >
                <PauseIcon className="w-4 h-4" />
                Stop
              </button>
            ) : (
              <button
                onClick={startStreaming}
                disabled={isLoading}
                className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 px-2 py-1 rounded-lg hover:bg-green-500/20"
              >
                <PlayIcon className="w-4 h-4" />
                Stream
              </button>
            )
          )}

          {/* Refresh button */}
          <button
            onClick={fetchLogs}
            disabled={isLoading || isStreaming}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {/* Download button */}
          {logs.length > 0 && (
            <button
              onClick={downloadLogs}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
          )}

          {/* Clear button */}
          {logs.length > 0 && (
            <button
              onClick={clearLogs}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <>
          {/* VM info */}
          {podName && (
            <div className="text-xs text-gray-400 mb-2">
              VM: <span className="font-mono text-gray-300">{podName.replace(/^virt-launcher-/, '')}</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-500/20 border border-red-600 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Not running message */}
          {!isRunning && (
            <div className="bg-yellow-500/20 border border-yellow-600 text-yellow-400 px-4 py-2 rounded-lg mb-4 text-sm">
              VM is not running. Start the VM to view live logs.
            </div>
          )}

          {/* Logs container */}
          <div
            ref={logsContainerRef}
            onScroll={handleScroll}
            className="bg-gray-900 text-gray-100 font-mono text-sm rounded-lg p-4 h-80 overflow-y-auto"
          >
            {isLoading && logs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <ArrowPathIcon className="w-6 h-6 animate-spin mr-2" />
                {isStreaming ? 'Connecting to log stream...' : 'Loading logs...'}
              </div>
            ) : logs.length === 0 && isStreaming && isConnected ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <ArrowPathIcon className="w-6 h-6 animate-spin mr-2" />
                Waiting for logs...
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <DocumentTextIcon className="w-12 h-12 mb-2" />
                <p>No logs available</p>
                {isRunning && !isStreaming && (
                  <button
                    onClick={startStreaming}
                    className="mt-2 text-green-400 hover:text-green-300 flex items-center gap-1"
                  >
                    <PlayIcon className="w-4 h-4" />
                    Start streaming logs
                  </button>
                )}
              </div>
            ) : (
              <>
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className="whitespace-pre-wrap break-all hover:bg-gray-800 px-1 -mx-1 rounded"
                  >
                    {log}
                  </div>
                ))}
                <div ref={logsEndRef} />
              </>
            )}
          </div>

          {/* Auto-scroll indicator */}
          {logs.length > 0 && !autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-2 text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <ChevronDownIcon className="w-3 h-3" />
              Scroll to bottom
            </button>
          )}

          {/* Status bar */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <span>{logs.length} log lines</span>
            {isStreaming && (
              <span className="flex items-center gap-1 text-green-600">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Streaming...
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VMLogs;
